"use client";

import { useRouter } from "next/navigation";
import { setMarketPreference } from "~/server/i18n/set-market";
import * as React from "react";

import { api } from "~/trpc/react";
import { CHECKOUT_PATH } from "~/config/storefront";
import { useCurrentPath, useLocale, useTranslator } from "~/i18n/provider";
import { useMarketPicker } from "~/components/storefront/market/market-picker-context";
import { recordDisclosureImpression } from "~/components/storefront/market/market-disclosure";
import { hasStoredConsent, onConsentChange } from "~/lib/analytics/consent";

/**
 * P3C · Either disclose an Automatic geo default, or offer a Suggest choice.
 * Manual device/account choices are filtered server-side before a suggestion
 * reaches this component. Apply writes both proposed axes together and refreshes
 * cart/checkout caches. During checkout, the existing picker explains the safe
 * return-to-basket path instead of changing an in-flight quote from a banner.
 * Where consent is configured, wait until that banner has been answered.
 */

/** Remembers the answer per country, so moving countries can ask again. */
const HINT_COOKIE = "vx_market_hint";

function readHint(): string | null {
  if (typeof document === "undefined") return null;
  const match = document.cookie.match(/(?:^|;\s*)vx_market_hint=([^;]*)/);
  return match?.[1] ? decodeURIComponent(match[1]) : null;
}

function rememberHint(country: string): void {
  // A year, matching the other preference cookies. Not httpOnly and not a
  // tracking identifier — it stores a country code the shopper was already
  // shown, which is why P11-DB01 treats it as consent-free.
  document.cookie = `${HINT_COOKIE}=${encodeURIComponent(country)}; path=/; max-age=${60 * 60 * 24 * 365}; samesite=lax`;
}

export function GeoSuggestBanner({
  country,
  regionName,
  regionCode = null,
  currency = null,
  suggestion = false,
  consentRequired = true,
}: {
  suggestion?: boolean;
  consentRequired?: boolean;
  country: string;
  regionName: string;
  /** What geo defaulted — the mapping an override would be judging (P11-23). */
  regionCode?: string | null;
  currency?: string | null;
}) {
  const utils = api.useUtils();
  const inCheckout = useCurrentPath().startsWith(CHECKOUT_PATH);
  const router = useRouter();
  const [applying, setApplying] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const t = useTranslator();
  const locale = useLocale();
  const picker = useMarketPicker();
  const [consentAnswered, setConsentAnswered] = React.useState(false);
  const [dismissed, setDismissed] = React.useState(true);

  React.useEffect(() => {
    // Both reads happen after mount on purpose: the server render must not
    // depend on either cookie, or the page could not be cached.
    const sync = () => setConsentAnswered(hasStoredConsent());
    sync();
    setDismissed(readHint() === country);
    return onConsentChange(sync);
  }, [country]);

  const visible = (!consentRequired || consentAnswered) && !dismissed;

  // P11-23 · The impression, recorded when the disclosure is actually on screen
  // — not when this component mounts, since it mounts and returns null for
  // every shopper who has dismissed it or has yet to answer consent.
  // `recordDisclosureImpression` dedups per country per session, because this
  // banner re-renders on every page view until dismissed.
  React.useEffect(() => {
    if (!visible) return;
    recordDisclosureImpression({ country, region: regionCode, currency });
  }, [visible, country, regionCode, currency]);

  if (!visible) return null;

  const dismiss = () => {
    rememberHint(country);
    setDismissed(true);
  };

  return (
    <div
      // Deliberately NOT role="status": the P11-20 currency-fallback notice
      // already owns that role on this page, and two would be ambiguous to a
      // screen reader and to any test locating by it.
      role="region"
      aria-label={t("locale.panelTitle")}
      data-testid="geo-suggest-banner"
      className="mx-auto flex max-w-3xl flex-wrap items-center gap-3 rounded-lg border border-border bg-surface-2 px-4 py-3 text-sm"
    >
      <p className="flex-1 text-fg-muted">
        {suggestion ? `It looks like you are in ${regionName}. ${currency ? `Use ${currency} for browsing and checkout?` : "Choose this pricing region?"} Delivery is checked from your address.` : t("locale.geoApplied", {
          region: regionName,
          currency: locale.currency,
        })}
      </p>
      {error && <p role="alert">{error}</p>}
      {suggestion && !inCheckout && <button type="button" disabled={applying} className="rounded border px-3 py-2 text-sm" onClick={async () => { setApplying(true); setError(null); try { const result = await setMarketPreference({ ...(currency ? { currency } : {}), ...(regionCode ? { region: regionCode } : {}) }); if (!result.ok) { setError("That choice is no longer available. Choose another market."); return; } await Promise.all([utils.cart.get.invalidate(), utils.checkout.get.invalidate()]); dismiss(); router.refresh(); } catch { setError("Could not save your choice. Please try again."); } finally { setApplying(false); } }}>{applying ? "Applying…" : "Apply suggestion"}</button>}
      <button
        type="button"
        onClick={() => picker?.openPicker()}
        className="rounded-lg bg-secondary px-3 py-1.5 text-xs font-semibold text-secondary-fg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)]"
      >
        {t("locale.geoChange")}
      </button>
      <button
        type="button"
        onClick={dismiss}
        className="rounded-lg px-3 py-1.5 text-xs font-semibold text-fg-muted hover:text-fg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)]"
      >
        {t("common.close")}
      </button>
    </div>
  );
}
